import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { formatDateForAPI } from "@/helpers/helpers";

// Zod schema for writer form validation
export const imroWriterFormSchema = z.object({
  firstname: z
    .string()
    .min(1, "First name is required")
    .refine((name) => {
      // Using the existing nameRegexTest function logic
      return /^[a-z\s'-]+$/i.test(name);
    }, "Invalid characters in first name"),

  lastname: z
    .string()
    .min(1, "Last name is required")
    .refine((name) => {
      return /^[a-z\s'-]+$/i.test(name);
    }, "Invalid characters in last name"),

  company: z.string().optional(),
  pseudonym: z.string().optional(),

  dateofbirth: z.union([
    z.date(),
    z.string().refine((val) => {
      // Allow empty string
      if (val === "")
        return true;
      // Use formatDateForAPI to validate DD/MM/YYYY format
      return formatDateForAPI(val) !== null;
    }, {
      message: "Please enter a valid date in DD/MM/YYYY format",
    }),
  ]).optional(),

  countryofbirth: z.string().min(1, "Country of birth is required"),

  country: z.string().min(1, "Country is required"),

  emailaddress: z
    .string()
    .min(1, "Email address is required")
    .email("Invalid email address format"),

  phonenumber: z
    .string()
    .min(1, "Phone number is required")
    .refine(
      val =>
      // Accepts international format (+), spaces, dashes, parentheses, and digits.
      // For better internationalisation, allow numbers with country codes, spaces, dashes, dots, parentheses.
      // Example: +44 20 7123 4567, +1 (555) 123-4567, 0491 570 156, etc.
        /^(?:\+?\d{1,4}[\s\-.]?)?(?:\(?\d{1,4}\)?)?[\d\s\-.]{5,}$/.test(val),
      "Invalid phone number format",
    ),

  address1: z.string().min(1, "Address line 1 is required"),
  address2: z.string().optional(),
  address3: z.string().optional(),
  address4: z.string().optional(),
  address5: z.string().optional(),

  taxarea: z.string().optional(),
  branchCountry: z.string().optional(),
  bankAccountCurrency: z.string().optional(),
  bankAccountName: z.string().optional(),
  bankName: z.string().optional(),
  accountNumber: z.string().optional(),
  iban: z.string().optional(),
  bicSwiftCode: z.string().optional(),
});

export type IMROWriterFormData = z.infer<typeof imroWriterFormSchema>;

type UseImroWriterFormOptions = {
  initialData?: Partial<IMROWriterFormData>;
};

// TSDoc: Custom hook for IMRO writer form handling
export function useImroWriterForm(options: UseImroWriterFormOptions = {}) {
  const { initialData } = options;

  // Setup react-hook-form with Zod validation
  const form = useForm<IMROWriterFormData>({
    resolver: zodResolver(imroWriterFormSchema),
    defaultValues: useMemo(() => {
      return {
        firstname: initialData?.firstname ?? "",
        lastname: initialData?.lastname ?? "",
        dateofbirth: initialData?.dateofbirth ?? undefined,
        countryofbirth: initialData?.countryofbirth ?? "",
        emailaddress: initialData?.emailaddress ?? "",
        phonenumber: initialData?.phonenumber ?? "",
        company: initialData?.company ?? "",
        pseudonym: initialData?.pseudonym ?? "",
        country: initialData?.country ?? "",
        address1: initialData?.address1 ?? "",
        address2: initialData?.address2 ?? "",
        address3: initialData?.address3 ?? "",
        address4: initialData?.address4 ?? "",
        address5: initialData?.address5 ?? "",
        taxarea: initialData?.taxarea ?? "",
      };
    }, [initialData]),
  });

  return {
    form,
  };
}
